/**
 * Collapsed-by-default raw JSON view of the run + cost events. The fallback
 * for power users / support staff who need everything verbatim. See spec
 * § "Component breakdown — RunDeveloperDetails" in
 * docs/superpowers/specs/2026-05-07-run-detail-polish-m35-design.md.
 */
import { useState } from "react";
import type { RunCostEventsResponse, RunResponse } from "@simpliwork/api-schema";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-muted-foreground">{label}</p>
      <pre className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-xs">
        {JSON.stringify(value ?? null, null, 2)}
      </pre>
    </div>
  );
}

export function RunDeveloperDetails({
  run,
  costEvents,
}: {
  run: RunResponse;
  costEvents: RunCostEventsResponse | undefined;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between"
          data-testid="dev-details-toggle"
        >
          <CardTitle className="text-sm font-semibold text-gray-700">
            Developer details
          </CardTitle>
          <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
        </button>
      </CardHeader>
      {open && (
        <CardContent className="flex flex-col gap-3">
          <JsonBlock label="Input" value={run.input} />
          <JsonBlock label="Output" value={run.output} />
          {run.error && <JsonBlock label="Error" value={run.error} />}
          {costEvents && <JsonBlock label="Cost events (raw)" value={costEvents.items} />}
        </CardContent>
      )}
    </Card>
  );
}
